// src/pages/TrainingList.js
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Code2, Smartphone, Layers, Search, Server, Cloud, Sparkles, MessageSquare, Bot,
  GraduationCap, Clock, Award, Users, ArrowRight, CheckCircle2
} from 'lucide-react';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TrainingEnrollForm from '../components/TrainingEnrollForm';
import { trainingPrograms, eligibleDegrees } from '../data/trainingData';
import '../styles/TrainingList.css';

const ICONS = { Code2, Smartphone, Layers, Search, Server, Cloud, Sparkles, MessageSquare, Bot };

const faqs = [
  {
    q: 'Who is eligible for these training and internship programs?',
    a: `Students currently pursuing or recently graduated from ${eligibleDegrees.join(', ')} or similar computer science / IT degrees are eligible to apply for any track.`
  },
  {
    q: 'What durations are available?',
    a: 'Every program is offered as a 6-week industrial training track or a 6-month internship track that goes deeper and includes a live, mentor-reviewed project.'
  },
  {
    q: 'Is the training online or in-person?',
    a: 'We offer both online and in-person options depending on the program and batch. Let us know your preference in the application form and we’ll confirm what’s available.'
  },
  {
    q: 'Will I get a certificate?',
    a: 'Yes, students who complete a program receive a Certificate of Training/Internship from EDSY Tech.'
  },
  {
    q: 'What are the fees?',
    a: 'Fees vary by program and duration. Apply below or contact us and our team will share current batch dates and fee details.'
  }
];

const TrainingList = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Training & Internship Programs | EDSY Tech',
    itemListElement: trainingPrograms.map((program, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `https://edsytech.com/training/${program.slug}`,
      name: program.title
    }))
  };

  return (
    <div className="training-list-page">
      <SEO
        title="Training & Internship Programs for BCA, B.Tech, MCA, M.Tech & B.Sc IT Students | EDSY Tech"
        description="6-week and 6-month industrial training & internship programs for BCA, B.Tech, MCA, M.Tech and B.Sc IT students in web development, mobile development, architecture, SEO, cloud infra, AI tools, prompt engineering and Claude (Anthropic) AI."
        canonicalPath="/training"
        keywords="training and internship, industrial training for students, 6 weeks training, 6 months internship, training for BCA students, training for B.Tech students, training for MCA students, web development training, cloud training, AI training, prompt engineering training, Claude AI training"
        jsonLd={jsonLd}
      />
      <Navbar />

      <section className="training-hero">
        <div className="training-hero-container">
          <span className="training-badge">
            <GraduationCap className="training-badge-icon" />
            Training & Internship
          </span>
          <h1 className="training-hero-title">
            Industrial Training & Internship Programs for College Students
          </h1>
          <p className="training-hero-subtitle">
            Hands-on, mentor-led 6-week and 6-month programs for BCA, B.Tech, MCA, M.Tech and B.Sc IT
            students &mdash; in web development, mobile development, architecture &amp; system design, SEO,
            infrastructure &amp; server administration, cloud infrastructure, AI tools, prompt engineering,
            and Claude (Anthropic) AI.
          </p>
          <div className="training-hero-actions">
            <a href="#programs" className="training-hero-cta-primary">
              Explore Programs
              <ArrowRight className="training-hero-cta-icon" />
            </a>
            <a href="#apply" className="training-hero-cta-secondary">
              Apply Now
            </a>
          </div>
        </div>
      </section>

      <section className="training-strip">
        <div className="training-strip-container">
          <div className="training-strip-item">
            <GraduationCap className="training-strip-icon" />
            <div>
              <div className="training-strip-title">Open to</div>
              <div className="training-strip-text">{eligibleDegrees.join(', ')}</div>
            </div>
          </div>
          <div className="training-strip-item">
            <Clock className="training-strip-icon" />
            <div>
              <div className="training-strip-title">Duration</div>
              <div className="training-strip-text">6 Weeks or 6 Months</div>
            </div>
          </div>
          <div className="training-strip-item">
            <Users className="training-strip-icon" />
            <div>
              <div className="training-strip-title">Format</div>
              <div className="training-strip-text">Mentor-led, project-based</div>
            </div>
          </div>
          <div className="training-strip-item">
            <Award className="training-strip-icon" />
            <div>
              <div className="training-strip-title">On completion</div>
              <div className="training-strip-text">Certificate of Training/Internship</div>
            </div>
          </div>
        </div>
      </section>

      <section id="programs" className="training-programs-section">
        <div className="training-programs-container">
          <div className="training-section-header">
            <span className="training-section-badge">Programs</span>
            <h2 className="training-section-title">Choose Your Track</h2>
            <p className="training-section-description">
              Nine tracks across development, architecture, infrastructure &amp; cloud, and AI &amp; emerging tech.
              Every track is available as a 6-week or 6-month program.
            </p>
          </div>

          <div className="training-programs-grid">
            {trainingPrograms.map((program) => {
              const Icon = ICONS[program.icon];
              return (
                <Link key={program.slug} to={`/training/${program.slug}`} className="training-program-card">
                  <div className="training-program-card-icon-wrapper">
                    {Icon && <Icon className="training-program-card-icon" />}
                  </div>
                  <span className="training-program-card-category">{program.category}</span>
                  <h3 className="training-program-card-title">{program.title}</h3>
                  <p className="training-program-card-tagline">{program.tagline}</p>
                  <div className="training-program-card-durations">
                    {program.durations.map((d) => (
                      <span key={d} className="training-program-card-duration-pill">{d}</span>
                    ))}
                  </div>
                  <span className="training-program-card-link">
                    View Curriculum
                    <ArrowRight className="training-program-card-link-icon" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="training-faq-section">
        <div className="training-faq-container">
          <div className="training-section-header">
            <span className="training-section-badge">FAQ</span>
            <h2 className="training-section-title">Common Questions</h2>
          </div>
          <div className="training-faq-list">
            {faqs.map((faq) => (
              <div key={faq.q} className="training-faq-item">
                <h3 className="training-faq-question">
                  <CheckCircle2 className="training-faq-icon" />
                  {faq.q}
                </h3>
                <p className="training-faq-answer">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="training-apply-section">
        <div className="training-apply-container">
          <div className="training-apply-intro">
            <span className="training-section-badge">Apply</span>
            <h2 className="training-section-title">Start Your Training or Internship</h2>
            <p className="training-section-description">
              Tell us which program and duration you&rsquo;re interested in &mdash; our team will get back to you
              with batch dates and fee details.
            </p>
          </div>
          <TrainingEnrollForm />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrainingList;
